import { useTranslation } from 'react-i18next'
import { forwardRef, useEffect, useImperativeHandle, useState, useRef, useContext } from 'react'
import { Button, IconButton, InputAdornment, LinearProgress, MenuItem, TextField } from '@mui/material'

import Dialog from '../common/dialog'
import { customersDocumentType } from '../../lib'
import HelpIcon from '../../assets/icons/help-icon.svg'
import { requestAccessToken } from '../../api/auth.api'
import { Customer, FormError, Payment } from '../../types'
import SearchIcon from '../../assets/icons/search-icon.svg'
import { AppStateContext, AuthContext } from '../../contexts'
import { RegisteredCustomerItem } from './registered-customer-item'
import { normalizeCustomerData, normalizeUnidentifiedCustomerData } from '../../utils'
import { getCustomerData, getUnidentifiedCustomerData } from '../../api/app-queries.api'
import { baseButtonBlueBg, baseButtonTextWhiteBg, baseButtonWhiteBg, textFieldWhiteBg } from '../../theme/custom-styles'

export const IdentificationDocumentNoveltyForm = forwardRef(
  (
    {
      errors,
      paymentData,
      customerDataSuggested,
      handleClearCustomerDataSuggested
    }: {
      errors: FormError
      customerDataSuggested: Customer | null
      handleClearCustomerDataSuggested: () => void
      paymentData: Payment | Partial<Payment> | null
    },
    ref
  ) => {
    const { isAuth } = useContext(AuthContext)
    const { customersBriefData } = useContext(AppStateContext)
    const { t } = useTranslation(['payments-to-review-management', 'individual-money-transfers-management', 'customers-management', 'common'])

    const customerToSearchRef = useRef<string>('')
    const currentCustomersDataStateRef = useRef<Array<Partial<Customer>>>([])

    const [customerId, setCustomerId] = useState<string>('')
    const [formDisabled, setFormDisabled] = useState<boolean>(true)
    const [customerToSearch, setCustomerToSearch] = useState<string>('')
    const [searchedInDatabase, setSearchedInDatabase] = useState<boolean>(false)
    const [searchingInDatabase, setSearchingInDatabase] = useState<boolean>(false)
    const [unidentifiedCustomerId, setUnidentifiedCustomerId] = useState<string>('')
    const [customersData, setCustomersData] = useState<Array<Partial<Customer>>>([])
    const [customersList, setCustomersList] = useState<Array<Partial<Customer>>>([])
    const [dialogCustomersIsOpen, setDialogCustomersIsOpen] = useState<boolean>(false)
    const [customerSuggestionAccepted, setCustomerSuggestionAccepted] = useState<boolean>(false)
    const [newCustomerData, setNewCustomerData] = useState<{
      documentType: string
      documentNumber: string
      registeredFullName: string
    }>({
      documentType: '',
      documentNumber: '',
      registeredFullName: ''
    })

    useImperativeHandle(
      ref,
      () => {
        return {
          customerId,
          newCustomerData,
          unidentifiedCustomerId,
          customerSuggestionAccepted
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [customerId, unidentifiedCustomerId, newCustomerData, customerSuggestionAccepted]
    )

    useEffect(() => {
      if (customersBriefData) {
        handleSyncCustomersDataFromRemoteDB()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customersBriefData])

    useEffect(() => {
      if (paymentData && Array.isArray(paymentData.novelties)) {
        const identificationDocumentNoveltiesDataToDisplay = []

        for (const element of paymentData.novelties) {
          const paymentNovelty = element
          const identificationDocumentNoveltyToDisplayData = {
            message: '',
            severity: ''
          }

          if (paymentNovelty.type === 'wrong-document-type') {
            identificationDocumentNoveltyToDisplayData.severity = 'warning'
            identificationDocumentNoveltyToDisplayData.message = t('noveltiesDescription.wrongDocumentType')

            identificationDocumentNoveltiesDataToDisplay.push(identificationDocumentNoveltyToDisplayData)
          }

          if (paymentNovelty.type === 'wrong-document-number') {
            identificationDocumentNoveltyToDisplayData.severity = 'danger'
            identificationDocumentNoveltyToDisplayData.message = t('noveltiesDescription.wrongDocumentNumber')

            identificationDocumentNoveltiesDataToDisplay.push(identificationDocumentNoveltyToDisplayData)
          }

          if (paymentNovelty.type === 'empty-document-number') {
            identificationDocumentNoveltyToDisplayData.severity = 'danger'
            identificationDocumentNoveltyToDisplayData.message = t('noveltiesDescription.emptyDocumentNumber')

            identificationDocumentNoveltiesDataToDisplay.push(identificationDocumentNoveltyToDisplayData)
          }

          if (paymentNovelty.type === 'empty-registered-name') {
            identificationDocumentNoveltyToDisplayData.severity = 'danger'
            identificationDocumentNoveltyToDisplayData.message = t('noveltiesDescription.emptyRegisteredName')

            identificationDocumentNoveltiesDataToDisplay.push(identificationDocumentNoveltyToDisplayData)
          }
        }

        if (paymentData.unidentifiedCustomerId) {
          handleGetUnidentifiedCustomerData(paymentData)
          setUnidentifiedCustomerId(paymentData.unidentifiedCustomerId)
        } else {
          handleGetCustomerData(paymentData)
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentData])

    const handleSetCustomerToSearch = async (customerToSearchData: string) => {
      setSearchingInDatabase(true)
      setCustomerToSearch(customerToSearchData)
      customerToSearchRef.current = customerToSearchData

      if (customerToSearchData === '') {
        setSearchedInDatabase(false)
        setSearchingInDatabase(false)
        customerToSearchRef.current = ''
        return setCustomersList([])
      }

      if (customerToSearchRef.current.length >= 2) {
        const customersDataFiltered = await handleGetDataWithRegexSearch(currentCustomersDataStateRef?.current)

        setSearchedInDatabase(true)
        setSearchingInDatabase(false)
        setCustomersList(customersDataFiltered)
      }
    }

    const handleGetDataWithRegexSearch = async (currentCustomersData: Array<Customer>) => {
      const customerToSearchData = customerToSearchRef.current

      const customersDataFilteredBySearch = currentCustomersData.filter((customerData) => {
        const regexToSearch = new RegExp(String(customerToSearchData), 'gi')

        if (regexToSearch.test(String(customerData.registeredFullName))) {
          return customerData
        }

        if (regexToSearch.test(String(customerData?.documentNumber))) {
          return customerData
        }

        let searchedByBankAccountNumber = false

        if (Array.isArray(customerData.bankAccountsData)) {
          for (const element of customerData.bankAccountsData) {
            const bankAccountData = element

            if (regexToSearch.test(String(bankAccountData.bankNumber))) {
              searchedByBankAccountNumber = true
            }
          }

          if (searchedByBankAccountNumber) {
            return customerData
          }
        }

        return false
      })

      return customersDataFilteredBySearch
    }

    const handleGetUnidentifiedCustomerData = async (paymentData: Payment | Partial<Payment>) => {
      const customerDataUpdated = { ...newCustomerData }
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const {
        data: { unidentifiedCustomerDataFromRemoteDb }
      } = await getUnidentifiedCustomerData(String(paymentData.unidentifiedCustomerId), accessToken)

      const unidentifiedCustomerData = normalizeUnidentifiedCustomerData(unidentifiedCustomerDataFromRemoteDb)

      if (unidentifiedCustomerData.registeredFullName) {
        customerDataUpdated.registeredFullName = unidentifiedCustomerData.registeredFullName
      }

      if (unidentifiedCustomerData.documentType && Object.keys(customersDocumentType).includes(unidentifiedCustomerData.documentType)) {
        customerDataUpdated.documentType = unidentifiedCustomerData.documentType
      }

      if (unidentifiedCustomerData.documentNumber) {
        customerDataUpdated.documentNumber = unidentifiedCustomerData.documentNumber
      }

      setNewCustomerData((customerDataPrevState) => {
        return {
          ...customerDataPrevState,
          ...customerDataUpdated
        }
      })

      handleSyncCustomersDataFromRemoteDB()
      setFormDisabled(false)
    }

    const handleGetCustomerData = async (paymentData: Payment | Partial<Payment>) => {
      const customerDataUpdated = { ...newCustomerData }
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const {
        data: { customerData }
      } = await getCustomerData(String(paymentData.customerId), accessToken)

      const selectedCustomerData = normalizeCustomerData(customerData)

      if (selectedCustomerData.registeredFullName) {
        customerDataUpdated.registeredFullName = selectedCustomerData.registeredFullName
      }

      if (selectedCustomerData.documentType && Object.keys(customersDocumentType).includes(selectedCustomerData.documentType)) {
        customerDataUpdated.documentType = selectedCustomerData.documentType
      }

      if (selectedCustomerData.documentNumber) {
        customerDataUpdated.documentNumber = selectedCustomerData.documentNumber
      }

      setNewCustomerData((customerDataPrevState) => {
        return {
          ...customerDataPrevState,
          ...customerDataUpdated
        }
      })

      handleSyncCustomersDataFromRemoteDB()
      setFormDisabled(false)
    }

    const handleSyncCustomersDataFromRemoteDB = () => {
      if (Array.isArray((customersBriefData as any).customersData)) {
        const customersNormalizedData = (customersBriefData as any).customersData.map((customerData: Customer) => normalizeCustomerData(customerData)) as unknown as Array<Customer | Partial<Customer>>
        customersNormalizedData.sort((thisCustomer: Customer, nextCustomer: Customer) => (thisCustomer?.registeredFullName || '').toLowerCase().localeCompare((nextCustomer?.registeredFullName || '').toLowerCase()))

        currentCustomersDataStateRef.current = customersNormalizedData
        setCustomersData(customersNormalizedData as Array<Customer>)
      }
    }

    const handleSelectRegisteredCustomer = (selectedCustomerData: Customer) => {
      setNewCustomerData({
        documentType: String(selectedCustomerData.documentType),
        documentNumber: String(selectedCustomerData.documentNumber),
        registeredFullName: String(selectedCustomerData.registeredFullName)
      })
      setCustomersList([])
      setCustomerToSearch('')
      setSearchedInDatabase(false)
      setSearchingInDatabase(false)
      setDialogCustomersIsOpen(false)
      setCustomerSuggestionAccepted(false)
      setCustomerId(String(selectedCustomerData._id))

      customerToSearchRef.current = ''
      handleClearCustomerDataSuggested()
    }

    return (
      <div className="novelty-form-container">
        <div className="full-name-text-field-container">
          <TextField
            fullWidth
            variant="standard"
            sx={textFieldWhiteBg}
            disabled={formDisabled}
            error={!!errors.registeredFullName}
            helperText={errors.registeredFullName}
            value={newCustomerData.registeredFullName}
            onChange={(event) =>
              setNewCustomerData((customerDataPrevState) => {
                return {
                  ...customerDataPrevState,
                  registeredFullName: event.target.value.toUpperCase()
                }
              })
            }
            label={`${t('resolveNoveltyModal.identificationDocument.registeredFullName')}`}
          />
        </div>
        <div className="identification-document-data-container">
          <div className="data-container">
            <TextField
              select
              fullWidth
              variant="standard"
              disabled={formDisabled}
              label={t('resolveNoveltyModal.identificationDocument.documentType')}
              sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' } }}
              error={!!errors.documentType}
              helperText={errors.documentType}
              value={newCustomerData.documentType}
              onChange={(event) => {
                setNewCustomerData((customerDataPrevState) => {
                  return {
                    ...customerDataPrevState,
                    documentType: event.target.value
                  }
                })
              }}
            >
              <MenuItem
                disabled
                value=""
              >
                {t('resolveNoveltyModal.identificationDocument.documentTypeSelectOptions.select')}
              </MenuItem>
              <MenuItem value="C">{t('resolveNoveltyModal.identificationDocument.documentTypeSelectOptions.cedulaCiudadania')}</MenuItem>
              <MenuItem value="N">{t('resolveNoveltyModal.identificationDocument.documentTypeSelectOptions.nit')}</MenuItem>
              <MenuItem value="P">{t('resolveNoveltyModal.identificationDocument.documentTypeSelectOptions.passport')}</MenuItem>
              <MenuItem value="E">{t('resolveNoveltyModal.identificationDocument.documentTypeSelectOptions.cedulaExtranjeria')}</MenuItem>
              <MenuItem value="PEP">{t('resolveNoveltyModal.identificationDocument.documentTypeSelectOptions.pep')}</MenuItem>
            </TextField>
          </div>
          <div className="data-container">
            <TextField
              fullWidth
              variant="standard"
              sx={textFieldWhiteBg}
              disabled={formDisabled}
              error={!!errors.documentNumber}
              helperText={errors.documentNumber}
              value={newCustomerData.documentNumber}
              onChange={(event) =>
                setNewCustomerData((customerDataPrevState) => {
                  return {
                    ...customerDataPrevState,
                    documentNumber: event.target.value
                  }
                })
              }
              label={`${t('resolveNoveltyModal.identificationDocument.documentNumber')}`}
            />
          </div>
        </div>
        {Array.isArray(customersData) && customersData.length > 0 && !customerDataSuggested && (
          <Button
            fullWidth
            variant="text"
            sx={baseButtonTextWhiteBg}
            className="actions-bar-button-action"
            onClick={() => {
              setDialogCustomersIsOpen(true)
            }}
          >
            <div className="actions-bar-button-action-text">{t('buttonsTexts.assingRegisteredCustomer')}</div>
          </Button>
        )}
        {customerDataSuggested && (
          <>
            <div
              style={{ flexDirection: 'column' }}
              className="identification-document-container"
            >
              <div
                className="subtitle"
                style={{ color: '#F59E0B', lineHeight: '150%' }}
              >
                {t('resolveNoveltyModal.identificationDocument.subtitleCustomerDataSuggested')}
              </div>
              <div className="identification-document-beneficiary-container">
                <div className="identification-document-row-item">
                  <div
                    className="subtitle"
                    style={{ textAlign: 'start' }}
                  >
                    <span>{t('resolveNoveltyModal.identificationDocument.registeredFullName')}</span>
                    <div>{customerDataSuggested.registeredFullName}</div>
                  </div>
                </div>
                <div className="identification-document-row-item">
                  <div className="left-content">
                    <span>{t('resolveNoveltyModal.identificationDocument.documentType')}</span>
                    <div>{(customersDocumentType as any)[customerDataSuggested.documentType ?? '3'].toUpperCase()}</div>
                  </div>
                  <div className="right-content">
                    <span>{t('resolveNoveltyModal.identificationDocument.documentNumber')}</span>
                    <div>{customerDataSuggested.documentNumber}</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="dialog-actions-container">
              <Button
                variant="contained"
                sx={baseButtonBlueBg}
                onClick={() => {
                  setNewCustomerData({
                    documentType: '',
                    documentNumber: '',
                    registeredFullName: ''
                  })
                  setCustomerSuggestionAccepted(false)
                  handleClearCustomerDataSuggested()
                }}
              >
                No, cancel
              </Button>
              <Button
                variant="contained"
                sx={baseButtonWhiteBg}
                onClick={() => {
                  setNewCustomerData({
                    documentType: String(customerDataSuggested.documentType),
                    documentNumber: String(customerDataSuggested.documentNumber),
                    registeredFullName: String(customerDataSuggested.registeredFullName)
                  })

                  handleClearCustomerDataSuggested()
                  setCustomerSuggestionAccepted(false)
                  setCustomerId(String(customerDataSuggested._id))
                }}
              >
                Yes, assign
              </Button>
            </div>
          </>
        )}
        {dialogCustomersIsOpen && (
          <Dialog
            hideActionsButtons
            actionButtonText=""
            handleDialogAction={() => console.log('entro')}
            dialogTitle={t('identificationDocumentModal.title')}
            handleDialogOpenState={(openState: boolean) => {
              if (openState === false) {
                handleSetCustomerToSearch('')
              }

              setDialogCustomersIsOpen(openState)
            }}
          >
            <div className="registered-bank-account-content-container">
              <div className="individual-money-transfers-beneficiary-searcher-container">
                <TextField
                  fullWidth
                  variant="standard"
                  sx={textFieldWhiteBg}
                  label={t('customerSearchField.placeholder')}
                  value={customerToSearch}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault()
                      handleSetCustomerToSearch(customerToSearch)
                    }
                  }}
                  onChange={(event) => {
                    handleSetCustomerToSearch(event.target.value)
                  }}
                  onBlur={() => {
                    if (customerToSearchRef.current === '') {
                      handleSetCustomerToSearch('')
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          aria-label="toggle password visibility"
                          sx={{ color: '#107569', right: '10px', bottom: '3px' }}
                        >
                          <img
                            src={SearchIcon}
                            alt="search-icon"
                          />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </div>
              {searchingInDatabase ? (
                <>
                  <div className="individual-money-transfers-searching-progress-bar">
                    <LinearProgress />
                  </div>
                  <div className="customer-no-data-found-no-registries-text">{t('searchingResultTexts.searchingInDatabase', { ns: 'individual-money-transfers-management' })}</div>
                </>
              ) : (
                <>
                  {searchedInDatabase && (
                    <>
                      {customersList.length === 0 ? (
                        <>
                          <div className="individual-money-transfers-beneficiary-subtitle">{t('homeModule.subtitleSearchNotFound', { ns: 'individual-money-transfers-management' })}</div>
                          <div className="customer-no-data-found-no-registries-text">
                            <img
                              src={HelpIcon}
                              alt="help-icon"
                            />
                          </div>
                          <div className="customer-no-data-found-no-registries-text">{t('searchingResultTexts.case2', { ns: 'customers-management' })}</div>
                        </>
                      ) : (
                        <>
                          <div
                            className="individual-money-transfers-beneficiary-subtitle"
                            style={{ textAlign: 'start', paddingLeft: '12px' }}
                          >
                            {t('homeModule.subtitleSearch', { ns: 'individual-money-transfers-management' })}
                          </div>
                          <div
                            style={{ maxHeight: '410px' }}
                            className="individual-money-transfers-latest-beneficiaries-container"
                          >
                            {customersList.map((customerData: Partial<Customer>, index: number) => {
                              return (
                                <RegisteredCustomerItem
                                  key={index}
                                  customerData={customerData}
                                  handleSelectRegisteredCustomer={handleSelectRegisteredCustomer}
                                />
                              )
                            })}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </Dialog>
        )}
      </div>
    )
  }
)
